import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { BaseProps, IAddonsProps } from '../common/props';
import classNames from 'classnames';
import * as Classes from '../common/classes';
import { InputAddons } from '../input-addons/InputAddons';
import { InputGroup, InputGroupProps } from '../input-group/InputGroup';

export interface Duration {
  minutes: number;
  hours: number;
  days: number;
  weeks: number;
}

export interface IDurationInputProps
  extends BaseProps,
    IAddonsProps,
    Omit<
      InputGroupProps,
      | 'children'
      | 'onChange'
      | 'asyncControl'
      | 'rightElement'
      | 'value'
      | 'onBlur'
      | 'type'
      | 'defaultValue'
    > {
  onChange?: (value: Duration | undefined) => void;
  onBlur?: (value: Duration | undefined) => void;
  value?: Duration;
  readOnly?: boolean;
  fullWidth?: boolean;
}

export type DurationInputProps = IDurationInputProps;

/**
 * Komponenta pro editaci doby trvání - podporovaný formát "1w 2d 3h 45m"
 */
export const DurationInput: React.FC<DurationInputProps> = ({
                                                              className,
                                                              onChange,
                                                              onBlur,
                                                              value,
                                                              addonAfter,
                                                              addonAfterCompact,
                                                              disabled,
                                                              readOnly,
                                                              fullWidth,
                                                              dataTest,
                                                              autoFocus,
                                                              ...other
                                                            }) => {
  const currEdited = useRef<Duration | undefined>(value);

  const formatValue = (value?: Duration): string => {
    console.log('FORMAT', value);
    if (!value || (!value.minutes && !value.hours && !value.days && !value.weeks)) { // není vyplněno ani jedno
      return '';
    }

    let strValue = '';
    if (value.weeks) {
      strValue += value.weeks + 'w ';
    }
    if (value.days) {
      strValue += value.days + 'd ';
    }
    if (value.hours) {
      strValue += value.hours + 'h ';
    }
    if (value.minutes) {
      strValue += value.minutes + 'm ';
    }

    console.log("FORMAT - string", strValue);
    // odstranění přebytečné mezery na konci řetězce
    return strValue.trim();
  };

  const parseValue = (value: string): Duration | undefined => {
    console.log('PARSE', value);
    if (!value) {
      return undefined;
    }

    // nahradíme čárku tečkou, abychom podpořili "1,5h"
    let normalized = value.replace(/,/g, '.');
    // pokud je zadáno jen číslo, přidáme "h" (hodiny)
    if (typeof parseFloat(normalized) === 'number') {
      normalized = normalized + 'h';
    }

    // regex: číslo (int nebo float) + volitelná mezera + jednotka
    const regex = /([\d.]+)\s*([wdhm])/gi;

    let match = regex.exec(normalized);
    if (match) {
      let minNum = 0, hourNum = 0, dayNum = 0, weekNum = 0;
      do {
        const numValue = parseFloat(match[1]);
        const unit = match[2].toLowerCase();

        switch (unit) {
          case 'w':
            weekNum = numValue;
            break;
          case 'd':
            dayNum = numValue;
            break;
          case 'h':
            hourNum = numValue;
            break;
          case 'm':
            minNum = numValue;
            break;
        }
      } while ((match = regex.exec(normalized)) !== null);

      return {
        minutes: minNum,
        hours: hourNum,
        days: dayNum,
        weeks: weekNum,
      };
    } else {
      return undefined;
    }
  };

  const [text, setText] = useState<string>(() => formatValue(value));

  useEffect(() => {
    if (value !== currEdited.current) {
      const newTextValue = formatValue(value);
      setText(newTextValue);
    }
  }, [value]);

  const compareValues = (v1?: Duration, v2?: Duration): boolean => {
    if (!v1 && !v2) {
      return true;
    } else if (!v1 || !v2) {
      return false;
    }

    return v1.minutes === v2.minutes && v1.hours === v2.hours && v1.days === v2.days && v1.weeks === v2.weeks;
  };

  const handleBlur = () => {
    const newText = formatValue(value);
    if (newText !== text) {
      setText(newText);
    }
    onBlur?.(value);
  };

  const handleChange = (e: any) => {
    let str = e.target.value;

    const newValue = parseValue(str);
    if (!compareValues(newValue, value)) {
      currEdited.current = newValue;
      onChange?.(newValue);
    }

    setText(str || '');
  };

  return (
    <InputAddons
      limitedSize
      addonAfter={addonAfter}
      addonAfterCompact={addonAfterCompact}
    >
      <InputGroup
        data-test={dataTest ? `input-${dataTest}` : undefined}
        className={classNames(
          className,
          Classes.FIELD_INPUT,
          Classes.DURATION_INPUT,
          {
            [Classes.FIELD_INPUT_DISABLED]: disabled,
            [Classes.FIELD_INPUT_READONLY]: readOnly,
            [Classes.LIMIT_DATE_TIME_SIZE]: !fullWidth,
          },
        )}
        onChange={handleChange}
        onBlur={handleBlur}
        value={text}
        {...other}
        disabled={disabled}
        readOnly={readOnly}
        autoFocus={autoFocus}
      />
    </InputAddons>
  );
};
